import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import {
  BodyText,
  FlexBox,
  FlexColumn,
  FlexRow,
  PageTile,
  SearchIcon,
  SubtitleText,
  SvgIcon,
} from '@make-software/cspr-design';
import { Trans, useTranslation } from 'react-i18next';
import { Deploy } from '@block-explorer/entities';
import {
  PaginationProps,
  TableSettings,
} from '../table-settings/table-settings';
import { NothingFound } from '../../scenes/NothingFoundScene';
import { ActivityFeedItem } from '../activity-feed-item/activity-feed-item';
import SectionTablePagination, {
  SectionPaginationType,
} from '../section-table/section-table-pagination';
import { TRANSACTION_LIST_PATH } from '../../router/paths';
import { useCustomHistory } from '../../router/navigation';
import { useAccountInfoContext } from '../../services/account-data-service';
import { ContractPackageHashFilter, FilterProps } from '../filters';
import { isNonNullable } from '../../utils/guards';
import { Link } from '@block-explorer/ui';

const StyledFlexColumn = styled(FlexColumn)(() => ({
  width: '100%',
}));

const TitlePageTile = styled(PageTile)(({ theme }) =>
  theme.withMedia({
    padding: '20px',
    height: ['fit-content', '80px'],
  })
);

const TitleContainer = styled.div<{ showShortPagination?: boolean }>(
  ({ theme, showShortPagination }) =>
    theme.withMedia({
      display: 'flex',
      flexDirection: showShortPagination ? 'row' : ['column', 'row'],
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
    })
);

const PaginationPageTile = styled(PageTile)(() => ({
  height: 'auto',
}));

const PaginationContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    '& > *': {
      marginTop: [8, 0, 0],
      height: 'auto',
      padding: 0,
      alignItems: 'center',
    },
    alignSelf: 'center',
  })
);

const BottomPaginationContainer = styled(FlexRow)<{
  showShortPagination?: boolean;
}>(({ showShortPagination }) => ({
  padding: showShortPagination ? '0 20px' : 0,
  justifyContent: showShortPagination ? 'space-between' : 'flex-end',
  alignItems: 'center',
  height: 'fit-content',
}));

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.fillPrimaryRed,
  },
  marginBottom: 20,
}));

export const FlexContainer = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column', 'row', 'row', 'row'],
  })
);

export const NoActivityFound = ({ hasFilters }: { hasFilters?: boolean }) => {
  const { t } = useTranslation();
  return (
    <FlexColumn align={'center'} justify={'center'} style={{ height: 400 }}>
      <StyledSvgIcon src={SearchIcon} size={60} />
      <BodyText scale={'lg'} lineHeight={'sm'} size={1}>
        {hasFilters
          ? t('No activity matches your selected filters.')
          : t('No activity to display.')}
      </BodyText>
    </FlexColumn>
  );
};

export const ActivityFeedListTable = ({
  data,
  loading,
  paginationProps,
}: {
  data: Deploy[] | null;
  loading: boolean;
  paginationProps: PaginationProps;
}) => {
  const { ...contextGetterFunctions } = useAccountInfoContext();

  const actionIdentificationHashes = {
    auction_manager_contract_hash: config.auction_manager_contract_hash,
    associated_keys_contract_hash: config.associated_keys_contract_hash,
    cspr_market_v1_contract_package_hash:
      config.cspr_market_v1_contract_package_hash,
    cspr_market_contract_package_hash: config.cspr_market_contract_package_hash,
    auction_pool_account_hash: config.auction_pool_account_hash,
  };

  if (loading || !data) {
    const rowsData = Array(paginationProps.perPage).fill(undefined);
    return (
      <StyledFlexColumn>
        {rowsData.map((item, index) => (
          <PageTile bottomGap={8} key={'row' + index}>
            <Skeleton height={125} />
          </PageTile>
        ))}
      </StyledFlexColumn>
    );
  }

  return data.map((deploy, idx) => (
    <ActivityFeedItem
      key={idx}
      deploy={deploy}
      loading={loading}
      actionIdentificationHashes={actionIdentificationHashes}
      {...contextGetterFunctions}
    />
  ));
};

const PaginationComponent = ({
  showShortPagination,
  paginationProps,
  paginationType,
  filterProps,
}) => {
  return showShortPagination ? (
    <SectionTablePagination
      {...paginationProps}
      paginationType={paginationType}
    />
  ) : (
    <FlexContainer>
      {filterProps && <ContractPackageHashFilter {...filterProps} isFullView />}
      <TableSettings
        {...paginationProps}
        hideRowsPerPage
        hideTotalCount
        hidePaginationOnError={false}
      />
    </FlexContainer>
  );
};

export interface ActivityFeedListProps {
  data: Deploy[] | null;
  loading: boolean;
  error: string | null;
  paginationProps: PaginationProps;
  filterProps?: FilterProps;
  paginationType?: SectionPaginationType;
  showViewAllLink?: boolean;
}

export const ActivityFeedList = (props: ActivityFeedListProps) => {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const {
    data,
    loading,
    error,
    paginationProps,
    filterProps,
    paginationType,
    showViewAllLink = false,
  } = props;

  const showShortPagination =
    paginationType === SectionPaginationType.extendedPagination;

  const hasFilters = Boolean(
    filterProps && Object.values(filterProps).some(isNonNullable)
  );

  if (!loading && data && data?.length === 0) {
    return (
      <StyledFlexColumn>
        <TitlePageTile bottomGap={8}>
          <TitleContainer>
            <SubtitleText size={1} scale={'md'} variation={'black'} tag={'h2'}>
              <Trans t={t}>Activity</Trans>
            </SubtitleText>
            <PaginationContainer>
              <PaginationComponent
                paginationType={paginationType}
                paginationProps={paginationProps}
                showShortPagination={showShortPagination}
                filterProps={filterProps}
              />
            </PaginationContainer>
          </TitleContainer>
        </TitlePageTile>
        <PageTile bottomGap={8}>
          <NoActivityFound hasFilters={hasFilters} />
        </PageTile>
      </StyledFlexColumn>
    );
  }

  if (error) {
    return (
      <StyledFlexColumn>
        <TitlePageTile bottomGap={8}>
          <TitleContainer>
            <SubtitleText size={1} scale={'md'} variation={'black'} tag={'h2'}>
              <Trans t={t}>Activity</Trans>
            </SubtitleText>
            <PaginationContainer>
              <PaginationComponent
                paginationType={paginationType}
                paginationProps={paginationProps}
                showShortPagination={showShortPagination}
                filterProps={filterProps}
              />
            </PaginationContainer>
          </TitleContainer>
        </TitlePageTile>
        <PageTile bottomGap={8}>
          <NothingFound />
        </PageTile>
      </StyledFlexColumn>
    );
  }

  return (
    <StyledFlexColumn>
      <TitlePageTile bottomGap={8}>
        <TitleContainer showShortPagination={showShortPagination}>
          <SubtitleText size={1} scale={'md'} variation={'black'} tag={'h2'}>
            <Trans t={t}>Activity</Trans>
          </SubtitleText>
          <PaginationContainer>
            <PaginationComponent
              paginationType={paginationType}
              paginationProps={paginationProps}
              showShortPagination={showShortPagination}
              filterProps={filterProps}
            />
          </PaginationContainer>
        </TitleContainer>
      </TitlePageTile>
      <ActivityFeedListTable
        data={data}
        loading={loading}
        paginationProps={paginationProps}
      />
      <PaginationPageTile>
        <BottomPaginationContainer showShortPagination={showShortPagination}>
          {showViewAllLink ? (
            <Link
              color={'primaryBlue'}
              onClick={() => history.push(TRANSACTION_LIST_PATH)}
            >
              <BodyText size={3} variation="blue" noWrap>
                <Trans t={t}>View all Transactions</Trans>
              </BodyText>
            </Link>
          ) : null}
          <PaginationComponent
            paginationType={paginationType}
            paginationProps={paginationProps}
            showShortPagination={showShortPagination}
          />
        </BottomPaginationContainer>
      </PaginationPageTile>
    </StyledFlexColumn>
  );
};

export default ActivityFeedList;
